<template>
<div class="sales-monitoring">
	<div class="container">
		<!-- Header -->
		<div class="page-header flex-center row">
			<h1 class="page-title col">{{ $t('Monitoring Sales') }}</h1>
			<div class="page-header-right col-sm-auto col-xs-12 d-flex justify-content-end">
				<smart-toggle
					v-tooltip="'Date Filter Toggle'"
					v-model="isDateFilterEnabled"
					@input="onChangeDateFilter"
				/>
				<!-- Date Filter -->
				<daterangepicker
					:presets="filterDatePresets"
					:start="since"
					:end="until"
					@selected="setFilterDate"
					:disabled="!isDateFilterEnabled"
				>
				</daterangepicker>
			</div>
		</div>
		<div class="page-content mt-4">
			<!-- Table Menu -->
			<div class="table-list-menu">
				<div class="row">
					<div class="col-sm-6">
						<div class="input-group" v-if="isAdminOnly">
							<!-- Search Sales or Seller -->
							<div class="input-group mb-3">
								<div class="input-group-prepend">
									<button
										class="btn btn-outline-secondary dropdown-toggle"
										type="button"
										data-toggle="dropdown"
										aria-haspopup="true"
										aria-expanded="false"
									>
										{{ selectedRoleLabel }}
									</button>
									<div class="dropdown-menu">
										<a class="dropdown-item" @click="assignRoleKey('sales')">Sales</a>
										<a class="dropdown-item" @click="assignRoleKey('dropshipper')">Seller</a>
									</div>
								</div>
								<!-- Search Sales -->
								<input
									v-if="selectedRoleKey === 'sales'"
									type="text"
									v-model="keyword"
									@input="onKeywordChange"
									class="form-control"
									aria-label="Text input with dropdown button"
									placeholder="Username / Email"
								/>
								<!-- Search Seller -->
								<multiselect
									v-else
									v-model="selectedSeller"
									:show-labels="false"
									class="form-control"
									track-by="_id"
									label="label"
									placeholder="Username / Email Seller"
									:options=optionSeller
									@search-change="fetchDataSeller"
								>
									<span slot="noResult">
										No options matching the query
									</span>
									<span slot="noOptions">
										No options available
									</span>
								</multiselect>
							</div>
						</div>
					</div>
					<div class="col-sm-6">
						<div class="table-filters d-flex align-items-center justify-content-end ml-a">
							<!-- Clear Filter -->
							<button type="button" class="btn btn-plain btn-sm" @click="clearFilter" v-if="isFilterOn">
								{{ $t('Clear Filter') }}
							</button>
							<button
								class="btn btn-light flex-center"
								:class="{ 'is-loading': isDownloading }"
								@click="exportSalesMonitoring"
								v-tooltip="dateFilterTooltip"
								:disabled="!isDateFilterEnabled"
							>
								<i class="material-icons">save_alt</i>
								<span class="mr-2">Download</span>
							</button>
						</div>
					</div>
				</div>
			</div>
			<div class="user-sales-list table-responsive">
				<table class="user-sales-list-table table table-primary table-striped">
					<thead>
						<tr>
							<th width="20%" class="text-left">
								ID Sales
							</th>
							<th width="20%" class="th-sort text-left" :class="sortClass('name')"
								@click="changeSorting('name')">
								Nama
							</th>
							<th width="20%" class="th-sort text-left" :class="sortClass('email')"
								@click="changeSorting('email')">
								Email
							</th>
							<th width="10%" class="th-sort text-left" :class="sortClass('total_order')"
								@click="changeSorting('total_order')">
								Total Order
							</th>
							<th width="10%" class="th-sort text-left" :class="sortClass('total_delivered')"
								@click="changeSorting('total_delivered')">
								Total Delivered
							</th>
							<th width="10%" class="th-sort text-left" :class="sortClass('total_returned')"
								@click="changeSorting('total_returned')">
								Total Returned
							</th>
							<th width="5%">
								Action
							</th>
						</tr>
					</thead>
					<tbody>
						<template v-for="(item, index) in items">
							<tr :key="index">
								<td>
									<span @click="copyId(item._id)" role="button">
										{{ item._id }}
									</span>
								</td>
								<td>
									<span class="text-primary cursor-pointer" @click="openDetailSales(item._id)">
										{{ item.name }}
									</span>
								</td>
								<td>
									{{ item.email }}
								</td>
								<td>
									<span class="text-primary cursor-pointer" @click="openDetailMonitoring(item._id)">
										{{ item.total_order }}
									</span>
								</td>
								<td>
									<span class="text-primary cursor-pointer" @click="openDetailMonitoring(item._id)">
										{{ item.total_delivered }}
									</span>
								</td>
								<td>
									<span class="text-primary cursor-pointer" @click="openDetailMonitoring(item._id)">
										{{ item.total_returned }}
									</span>
								</td>
								<td class="text-center">
									<!-- Download -->
									<button
										type="button"
										class="btn btn-sm btn-primary"
										:class="{ 'is-loading': isDownloading && itemIndex === index }"
										@click="exportSalesMonitoring('dropshipper', item._id, index)"
										v-tooltip="dateFilterTooltip"
										:disabled="!isDateFilterEnabled"
									>
										<i class="material-icons">save_alt</i>
										<!-- <span class="mr-2">{{ $t('Download') }}</span> -->
									</button>
								</td>
							</tr>
						</template>
					</tbody>
				</table>
				<div class="table-fetch-status" v-if="isFetching">
					<div class="is-loading large dark m-auto"></div>
				</div>
				<!-- Empty -->
				<div class="list-empty" v-if="items.length === 0 && isFetching === false">
					<i class="material-icons mr-2">error_outline</i>
					<span v-if="keyword === ''">There is no Sales Found yet.</span>
					<span v-else>No Sales was found</span>
				</div>
			</div>
		</div>
	</div>
</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import {
	copyToClipboard,
	duplicateVar,
	getDateRange,
	downloadFile,
} from '@/lib/helper';
import adminApi from '@/api/admin';
import Daterangepicker from '@/components/Daterangepicker.vue';
import SmartToggle from '@/components/SmartToggle.vue';

const defaultFilterDate = getDateRange();

export default {
	name: 'AdminSalesMonitoring',
	components: {
		Daterangepicker,
		SmartToggle,
	},
	data() {
		return {
			limit: 25,
			sort_by: 'created_at',
			sort: 'desc',
			keyword: '',
			filterDate: defaultFilterDate,
			selectedRoleKey: 'sales',
			roleTypes: [
				{
					key: 'sales',
					label: 'Sales',
				},
				{
					key: 'dropshipper',
					label: 'Seller',
				},
			],
			isDownloading: false,
			itemIndex: null,
			isDateFilterEnabled: true,
			selectedSeller: null,
			optionSeller: [],
			isSearchingSeller: false,
			searchSeller: '',
		};
	},
	methods: {
		...mapActions({
			getSalesMonitoring: 'salesMonitoring/getSalesMonitoring',
			resetSalesMonitoring: 'salesMonitoring/resetSalesMonitoring',
		}),
		getFetchParams() {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = this.moment(this.filterDate.start).format(dateFormat);
			const endDate = this.moment(this.filterDate.end).format(dateFormat);

			const params = {
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
				page: this.page,
			};

			if (this.isDateFilterEnabled) {
				params.since = startDate;
				params.until = endDate;
			}

			if (this.keyword && this.keyword !== '') {
				params.keyword = this.keyword;
			}

			if (this.selectedRoleKey && this.selectedRoleKey !== '') {
				params.search_type = this.selectedRoleKey;
			}

			if (this.selectedSeller) {
				params.seller_id = this.selectedSeller?._id;
			}

			return params;
		},
		fetchItems(clear = false) {
			if (clear) {
				this.resetSalesMonitoring();
			}
			const params = this.getFetchParams();
			this.getSalesMonitoring(params);
		},
		handleScroll() {
			const pageHeight = document.documentElement.offsetHeight;
			const windowHeight = window.innerHeight;
			const scrollPosition = window.scrollY;
			const offset = 50;
			const isOnBottom = pageHeight - offset <= windowHeight + scrollPosition;
			if (isOnBottom) {
				if (!this.isFetching && !this.isAll) {
					this.fetchItems(false);
				}
			}
		},
		changeSorting(key) {
			const isSame = this.sort_by === key;
			this.sort_by = key;
			const sort = isSame && this.sort === 'asc' ? 'desc' : 'asc';
			this.sort = sort;
			// Re-fetch
			this.fetchItems(true);
		},
		sortClass(key) {
			const sameKey = this.sort_by === key;
			const className = sameKey ? `th-sort-${this.sort}` : '';
			return className;
		},
		applyFilter() {
			this.fetchItems(true);
		},
		onKeywordChange() {
			const self = this;
			this.ooDelay(() => {
				self.applyFilter();
			}, 1000);
		},
		clearFilter() {
			this.keyword = '';
			this.selectedSeller = null;
			this.applyFilter();
		},
		copyId(item) {
			copyToClipboard(item);
			// toast notification
			this.$notify({
				group: 'app',
				title: 'ID Copied',
				text: item,
				type: 'success',
			});
		},
		initFilterDate() {
			if (this.since && this.since !== '' && this.until && this.until !== '') {
				const since = this.moment(this.since);
				const until = this.moment(this.until);
				const filterDate = {
					start: since,
					end: until,
				};
				this.filterDate = filterDate;
			}
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const format = 'YYYY-MM-DD';
			const since = this.moment(filterDate.start).format(format);
			const until = this.moment(filterDate.end).format(format);
			const query = duplicateVar(this.$route.query);
			query.since = since;
			query.until = until;
			const params = { query };
			this.$router.push(params);
			this.fetchItems(true);
		},
		openDetailSales(salesId) {
			const salesDetailUrl = this.$router.resolve({
				name: 'sales-detail',
				params: {
					sales_id: salesId,
				},
			}).href;

			window.open(salesDetailUrl, '_blank');
		},
		openDetailMonitoring(salesId) {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = this.moment(this.filterDate.start).format(dateFormat);
			const endDate = this.moment(this.filterDate.end).format(dateFormat);

			const monitoringDetailUrl = this.$router.resolve({
				name: 'monitoring-sales-detail',
				params: {
					sales_id: salesId,
				},
				query: {
					since: startDate,
					until: endDate,
					is_date_filter_enabled: this.isDateFilterEnabled,
				},
			}).href;

			window.open(monitoringDetailUrl, '_blank');
		},
		assignRoleKey(key) {
			this.selectedRoleKey = key;
		},
		exportSalesMonitoring(type = 'sales', salesId = null, itemIndex = null) {
			this.isDownloading = true;
			this.itemIndex = itemIndex;

			const params = this.getFetchParams();

			params.type = type;
			params.sales_id = salesId;
			params.is_export = true;

			const callback = (response) => {
				const url = response.data;
				if (url && url !== '') {
					downloadFile(url);
				} else {
					this.$notify({
						group: 'app',
						type: 'warn',
						title: 'Sales Monitoring',
						text: 'Download Failed',
					});
				}
				this.isDownloading = false;
				this.itemIndex = null;
			};

			const errorCallback = () => {
				this.isDownloading = false;
				this.itemIndex = null;
			};

			adminApi.exportSalesMonitoring(params, callback, errorCallback);
		},
		onChangeDateFilter(value) {
			this.isDateFilterEnabled = value;
			this.applyFilter();
		},
		fetchDataSeller(query) {
			this.isSearchingSeller = true;

			const params = {
				keyword: query,
				role: 'owner',
				owner_type: 'dropshipper',
			};

			const callback = (response) => {
				this.optionSeller = response.data;
				this.isSearchingSeller = false;
			};

			const errorCallback = () => {
				this.isSearchingSeller = false;
			};

			this.ooDelay(() => {
				if (query !== '') {
					adminApi.searchUser(params, callback, errorCallback);
				}
			}, 1000);
		},
	},
	computed: {
		...mapGetters({
			items: 'salesMonitoring/allSalesMonitoring',
			isFetching: 'salesMonitoring/isFetching',
			isAll: 'salesMonitoring/isAll',
			page: 'salesMonitoring/page',
			isFetched: 'salesMonitoring/isFetched',
			filterDatePresets: 'filterDatePresets',
			isRoleSuperAdmin: 'isRoleSuperAdmin',
			isRoleAdmin: 'isRoleAdmin',
		}),
		since() {
			const since = this.$route.query.since;
			return since;
		},
		until() {
			const until = this.$route.query.until;
			return until;
		},
		isFilterOn() {
			return this.keyword !== '' || this.selectedSeller;
		},
		selectedRoleLabel() {
			let label = '';

			const selectedRole = this.roleTypes.find(role => role.key === this.selectedRoleKey);
			if (selectedRole && selectedRole.label) {
				label = selectedRole.label;
			}

			return label;
		},
		dateFilterTooltip() {
			const tooltip = !this.isDateFilterEnabled ? 'Aktifkan Dahulu Filter Tanggal' : '';

			return tooltip;
		},
		isAdminOnly() {
			const isAdmin = this.isRoleAdmin || this.isRoleSuperAdmin;

			return isAdmin;
		},
	},
	watch: {
		selectedRoleKey() {
			if (this.keyword !== '') {
				this.onKeywordChange();
			} else if (this.selectedSeller) {
				this.applyFilter();
			}
		},
		selectedSeller() {
			this.applyFilter();
		},
	},
	created() {
		this.resetSalesMonitoring();
		this.initFilterDate();
		this.fetchItems();
		window.addEventListener('scroll', this.handleScroll);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
	},
};
</script>

<style lang="scss" scoped>
.btn-outline-secondary {
	border: 1px solid #ced4da;
}

.input-group > .multiselect:last-child .multiselect__tags {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
}
</style>
